import ApiService from './ApiService'

export interface AuditLogEntry {
  id: number
  tableName: string
  recordId: number
  action: 'INSERT' | 'UPDATE' | 'DELETE' | string
  oldValue: Record<string, any> | null
  newValue: Record<string, any> | null
  diff: Record<string, { old: any; new: any }> | null
  changedById: number | null
  changedAt: string
  tenantId: number
  ipAddress?: string | null
  userAgent?: string | null
  restoredFromId?: number | string | null
  clusterCount?: number
  ChangedBy?: {
    id: number
    name: string
    email: string
    picture?: string | null
  } | null
  Tenant?: {
    id: number
    companyName?: string
    name?: string
  } | null
}

export interface DeletedRecordsResponse {
  total: number
  page: number
  limit: number
  totalPages: number
  records: AuditLogEntry[]
}

export interface AuditLogAdminFilters {
  tenantId?: number
  tableName?: string
  action?: string
  userId?: number
  search?: string
  startDate?: string
  endDate?: string
  page?: number
  limit?: number
}

export interface AuditRetentionStats {
  activeLogsCount: number
  archivedLogsCount: number
  deletedLogsCount: number
  oldestActiveDate: string | null
  oldestArchivedDate: string | null
  hotWindowDays: number
  archiveWindowDays: number
  estimatedSpaceSavedKb: number
}

export interface AuditArchiveSummary {
  monthYear: string
  totalRecords: number
  tablesCount: number
  oldestDate: string
  newestDate: string
}

export interface AuditRetentionTaskItem {
  id: number
  taskType: string
  status: 'RUNNING' | 'COMPLETED' | 'FAILED' | string
  recordsCount: number
  details?: string | null
  startedAt: string
  completedAt?: string | null
  triggeredById?: number | null
  tenantId?: number | null
  TriggeredBy?: {
    id: number
    name: string
    email: string
  } | null
}

export interface UnarchiveRequestParams {
  startDate: string
  endDate: string
  tableName?: string
  tenantId?: number
}

class AuditApiService {
  private static getTenantId(): number | undefined {
    const stored = localStorage.getItem('tenantId') || localStorage.getItem('tenant_id')
    if (stored && !isNaN(Number(stored))) {
      return Number(stored)
    }
    return undefined
  }

  static async getRecordAuditHistory(tableName: string, recordId: number, tenantId?: number): Promise<AuditLogEntry[]> {
    const resolvedTenant = tenantId || this.getTenantId()
    const response = await ApiService.get(
      `/automation/audit/records/${tableName}/${recordId}`,
      resolvedTenant ? { tenantId: resolvedTenant } : undefined
    )
    return response.data || []
  }

  static async getDeletedRecords(tableName?: string, page = 1, limit = 20, tenantId?: number): Promise<DeletedRecordsResponse> {
    const resolvedTenant = tenantId || this.getTenantId()
    const url = tableName ? `/automation/audit/deleted/${tableName}` : '/automation/audit/deleted'
    const params: any = { page, limit }
    if (resolvedTenant) {
      params.tenantId = resolvedTenant
    }
    const response = await ApiService.get(url, params)
    return response.data || { total: 0, page: 1, limit: 20, totalPages: 0, records: [] }
  }

  static async restoreDeletedRecord(id: number) {
    const response = await ApiService.post(`/automation/audit/restore/${id}`)
    return response.data
  }

  static async getAdminLiveLogs(filters: AuditLogAdminFilters = {}): Promise<DeletedRecordsResponse> {
    const response = await ApiService.get('/automation/audit/admin/live', filters)
    return response.data || { total: 0, page: 1, limit: 20, totalPages: 0, records: [] }
  }

  static async getAdminArchives(tenantId?: number): Promise<AuditArchiveSummary[]> {
    const response = await ApiService.get('/automation/audit/admin/archives', { tenantId })
    return response.data || []
  }

  static async getAdminArchivedRecords(filters: AuditLogAdminFilters = {}): Promise<DeletedRecordsResponse> {
    const response = await ApiService.get('/automation/audit/admin/archives/records', filters)
    return response.data || { total: 0, page: 1, limit: 20, totalPages: 0, records: [] }
  }

  static async runArchiveTask(days = 30, tenantId?: number) {
    const response = await ApiService.post(`/automation/audit/admin/archive/run?days=${days}${tenantId ? `&tenantId=${tenantId}` : ''}`)
    return response.data
  }

  static async unarchivePeriod(params: UnarchiveRequestParams) {
    const response = await ApiService.post('/automation/audit/admin/archive/unarchive', params)
    return response.data
  }

  static async runPurgeTask(months = 6, tenantId?: number) {
    const response = await ApiService.post(`/automation/audit/admin/purge/run?months=${months}${tenantId ? `&tenantId=${tenantId}` : ''}`)
    return response.data
  }

  static async getAdminStats(tenantId?: number): Promise<AuditRetentionStats> {
    const response = await ApiService.get('/automation/audit/admin/stats', { tenantId })
    return response.data || {
      activeLogsCount: 0,
      archivedLogsCount: 0,
      deletedLogsCount: 0,
      oldestActiveDate: null,
      oldestArchivedDate: null,
      hotWindowDays: 30,
      archiveWindowDays: 180,
      estimatedSpaceSavedKb: 0,
    }
  }

  static async getRetentionTasks(tenantId?: number, page = 1, limit = 20) {
    const response = await ApiService.get('/automation/audit/admin/tasks', { tenantId, page, limit })
    return response.data || { total: 0, page: 1, limit: 20, totalPages: 0, tasks: [] }
  }
}

export default AuditApiService
